import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

const DIRECTION_OPTIONS = [
  { key: "above", label: "Above" },
  { key: "below", label: "Below" },
]

interface CustomAudioFrequencyConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const CustomAudioFrequencyConfig: React.FC<
  CustomAudioFrequencyConfigProps
> = ({ metricRecordData, onChange }) => {
  const direction = metricRecordData.metric_metadata?.direction || "above"

  const handleFrequencyThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        frequency_threshold: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleDirectionChange = (selectedDirection: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        direction: selectedDirection as "above" | "below",
      },
    })
  }

  const handlePercentAboveThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        percent_above_threshold: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Frequency Threshold (Hz){" "}
            <span className="text-coval-danger-text">*</span>
          </label>
        </div>
        <input
          type="number"
          step="50"
          min="0"
          className={TEXT_INPUT_STYLES}
          value={metricRecordData.metric_metadata?.frequency_threshold || ""}
          onChange={handleFrequencyThresholdChange}
          placeholder="300"
        />
        <p className="text-xs text-coval-text-muted">
          The frequency cutoff in Hz for the filter.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Direction</label>
        </div>
        <Select value={direction} onValueChange={handleDirectionChange}>
          <SelectTrigger aria-label="Select direction">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIRECTION_OPTIONS.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Measure the percentage of voiced segments above or below the frequency
          threshold.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Display Cutoff (%) <span className="text-coval-danger-text">*</span>
          </label>
        </div>
        <input
          type="number"
          step="5"
          min="0"
          max="100"
          className={TEXT_INPUT_STYLES}
          value={
            metricRecordData.metric_metadata?.percent_above_threshold ?? ""
          }
          onChange={handlePercentAboveThresholdChange}
          placeholder="50"
        />
        <p className="text-xs text-coval-text-muted">
          Percentage needed to display as "Passes Filter". Below half this value
          displays as "Fails Filter".
        </p>
      </div>
    </div>
  )
}
